// search.ts — the console's seat on THE text/search primitive (owner item D,
// bridge-ui-polish; docs/bridge-ui-polish.md).
//
// "The search should be in the same place (above the left side of the grid)
// on all pages. The search box should have an icon, and needs full emotive
// design through the animations." So the box is a COMPONENT, not a class
// convention: releases, users, audit and templates all mount THIS, leftmost
// in the .hc-toolbar row directly above their grid.
//
// PR-3a moved the machine itself into the shared client layer
// (pi-stack/src/client/form-controls.ts, `mkFcText` with `variant: 'search'`)
// so the Helm's own search fields ride the same interaction ladder — the
// clear affordance, the transient typing state, Escape-clears-first — rather
// than each surface growing its own. This file is the console's ADAPTER: it
// keeps `mkSearchBox`'s signature and its `.hc-searchbox` / `.hc-search` /
// `.hc-sb-*` class names, so every selector the suites, the e2e specs and
// the CSS interaction ladder address is unchanged. Those classes are LAYOUT
// hooks now; the field's paint lives in the fc-component-block.
//
// What "matches" stays the CALLER's decision: users/home/templates filter
// client-side, audit debounces a server-side `q`. The input keeps the
// caller's historical id (#hcUserSearch, #hcAuditQ, #hcCtSearch) so e2e
// specs and saved muscle memory survive the re-skin.

import { mkFcText, type FcTextCtl, FC_TYPING_MS } from './form-controls.js';

export interface SearchBoxOpts {
  /** The INPUT element's id — callers keep their historical ids. */
  id: string;
  placeholder: string;
  /** Accessible name for the input (aria-label). */
  label: string;
  /** One lane for every value change: keystrokes AND the clear button. */
  onInput: (value: string) => void;
}

export interface SearchBoxCtl {
  el: HTMLElement;
  input: HTMLInputElement;
  value(): string;
  /** Programmatic clear — same lane as the clear button (fires onInput). */
  clear(): void;
}

/** How long the 'typing' state outlives the last keystroke. Exported so the
 * headless suite pins the state is TRANSIENT, not sticky. Re-exported from
 * the primitive so the console and the Helm can never disagree about it. */
export const SEARCH_TYPING_MS = FC_TYPING_MS;

/** The console's search-box layout hooks, handed to the primitive as a skin.
 *  Stamped BESIDE the fc-* classes — never instead of them. */
export const HC_SEARCH_SKIN = {
  wrap: 'hc-searchbox',
  input: 'hc-search',
  ico: 'hc-sb-ico',
  clear: 'hc-sb-clear',
  has: 'hc-sb-has',
  typing: 'hc-sb-typing',
} as const;

export function mkSearchBox(opts: SearchBoxOpts): SearchBoxCtl {
  const ctl: FcTextCtl = mkFcText({
    id: opts.id,
    label: opts.label,
    placeholder: opts.placeholder,
    variant: 'search',
    skin: HC_SEARCH_SKIN,
    onInput: opts.onInput,
  });
  return { el: ctl.el, input: ctl.input, value: () => ctl.value, clear: () => ctl.clear() };
}
